import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CreditCard, Landmark } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { CTAButton } from "@/components/landing/CTAButton";
import { PRICES } from "@/lib/site";

const badgeStyle = {
  "ХИТ": "gold-gradient text-[#0B0B1F]",
  "-30%": "bg-[#5B3A78] text-[#F0C674] border border-[#F0C674]/40",
};

const runtimeConfig = (typeof window !== "undefined" && window.APP_CONFIG) || {};
const API = `${runtimeConfig.API_BASE || "https://tarovarvara.ru"}/api`;

export const Pricing = () => {
  // Живые цены из БД (та же таблица Product, что правит владелец в админке).
  // Пока не загрузились или API недоступен — показываем запасные из PRICES.
  const [livePrices, setLivePrices] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/pricing`)
      .then((res) => {
        if (!mounted || !Array.isArray(res.data)) return;
        const byCode = new Map(res.data.map((p) => [p.code, p.priceRub]));
        setLivePrices(byCode);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const prices = PRICES.map((p) => {
    const live = livePrices?.get(p.code);
    return live != null ? { ...p, price: `${live} ₽` } : p;
  });

  return (
    <section id="pricing" className="relative py-24 sm:py-32" data-testid="pricing-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#F0C674]/80">
            Глава 03 — Тарифы
          </p>
          <h2 className="font-serif text-4xl font-bold text-[#EDE6D3] sm:text-5xl lg:text-6xl">
            Цены на <span className="text-gold-gradient">разборы</span>
          </h2>
          <p className="mt-4 text-[#EDE6D3]/60">
            Выбирайте разбор по интересу.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {prices.map((p, i) => {
            const highlight = p.badge === "ХИТ";
            return (
              <Reveal key={p.title} delay={(i % 4) * 0.07}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className={`relative flex h-full flex-col rounded-2xl p-6 ${
                    highlight
                      ? "chase-ring chase-ring--hot border-2 border-[#D4AF6E] bg-surface glow-gold"
                      : "border border-[#D4AF6E]/12 bg-surface hover:border-[#D4AF6E]/30"
                  }`}
                  data-testid={`price-card-${i}`}
                >
                  {p.badge && (
                    <span
                      className={`absolute -top-3 right-5 rounded-full px-3 py-1 text-xs font-bold tracking-wide ${badgeStyle[p.badge]}`}
                      data-testid={`price-badge-${i}`}
                    >
                      {p.badge}
                    </span>
                  )}
                  <h3 className="font-serif text-2xl font-semibold text-[#EDE6D3]">
                    {p.title}
                  </h3>
                  <p className="mt-1 flex-1 text-sm text-[#EDE6D3]/55">{p.desc}</p>
                  <div className="mt-5 font-serif text-4xl font-bold text-gold-gradient">
                    {p.price}
                  </div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>

        {/* Payment row */}
        <Reveal delay={0.1} className="mt-10 flex flex-col items-center gap-4 text-center">
          <p className="flex items-center gap-2 text-sm text-[#EDE6D3]/70">
            <CreditCard className="h-4 w-4 text-[#F0C674]" />
            Оплата картой или через СБП прямо в боте
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {["Мир", "Visa", "MC"].map((m) => (
              <span
                key={m}
                className="rounded-lg border border-[#D4AF6E]/20 bg-[#111133] px-4 py-2 text-xs font-semibold text-[#EDE6D3]/80"
              >
                {m}
              </span>
            ))}
            <span className="flex items-center gap-1.5 rounded-lg border border-[#D4AF6E]/20 bg-[#111133] px-4 py-2 text-xs font-semibold text-[#EDE6D3]/80">
              <Landmark className="h-3.5 w-3.5 text-[#F0C674]" /> СБП
            </span>
          </div>
          <div className="mt-4">
            <CTAButton goal="cta_max_bot" testId="pricing-cta-bot">
              Начать разбор в MAX
            </CTAButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
